import React, { useEffect, useRef } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Input, message } from 'antd';

import { VALIDATION_MESSAGES, VALIDATION_RULES } from '@/shared/lib/constants/form';
import { Button } from '@/shared/ui/Button';
import { Icon } from '@/shared/ui/Icon';

import styles from './ExperimentTagInput.module.css';

const MAX_TAGS = 10;
const DUPLICATE_ERROR_TEXT = VALIDATION_MESSAGES.NAME_ALREADY_EXISTS;
const PLACEHOLDER = '태그를 입력해 주세요';

type ExperimentTagInputProps = {
  value?: string[];
  onChange?: (tags: string[]) => void;
  maxLength?: number;
  placeholder?: string;
  minTags?: number;
  scrollable?: boolean;
  maxHeight?: string;
  showAddButton?: boolean;
  errorText?: string;
  onValidationChange?: (isValid: boolean) => void;
};

export const ExperimentTagInput: React.FC<ExperimentTagInputProps> = ({
  value,
  onChange,
  maxLength = VALIDATION_RULES.TAG_NAME_MAX_LENGTH,
  placeholder = PLACEHOLDER,
  minTags = 1,
  scrollable = false,
  maxHeight,
  showAddButton = true,
  errorText,
  onValidationChange,
}) => {
  const tagInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const isAddingTagRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasDuplicates = (tags: string[]) => {
    const nonEmptyTags = tags.filter((tag) => tag.trim() !== '');
    return new Set(nonEmptyTags).size !== nonEmptyTags.length;
  };

  useEffect(() => {
    if (value && onValidationChange) {
      const isValid = !hasDuplicates(value);
      onValidationChange(isValid);
    }
  }, [value, onValidationChange]);

  useEffect(() => {
    if (!value) return;
    tagInputRefs.current = tagInputRefs.current.slice(0, value.length);
  }, [value]);

  const handleAddTag = () => {
    if (!onChange || !value) return;
    if (isAddingTagRef.current) return;

    if (value.length >= MAX_TAGS) {
      message.warning(`최대 ${MAX_TAGS}개까지만 추가 가능합니다.`);
      return;
    }

    isAddingTagRef.current = true;
    onChange([...value, '']);

    setTimeout(() => {
      const newIndex = value.length;
      tagInputRefs.current[newIndex]?.focus();

      if (scrollable && containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }

      isAddingTagRef.current = false;
    }, 100);
  };

  const handleTagChange = (index: number, newValue: string) => {
    if (!onChange || !value) return;
    const newTags = [...value];
    newTags[index] = newValue;
    onChange(newTags);
  };

  const handleRemoveTag = (index: number) => {
    if (!onChange || !value) return;
    if (value.length <= minTags) return;

    const newTags = value.filter((_, i) => i !== index);
    onChange(newTags);

    setTimeout(() => {
      const focusIndex = index > 0 ? index - 1 : 0;
      const targetInput = tagInputRefs.current[focusIndex];
      if (targetInput) {
        targetInput.focus();
        const length = targetInput.value.length;
        targetInput.setSelectionRange(length, length);
      }
    }, 0);
  };

  const handleTagKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && value && value.length > minTags) {
      const currentValue = value[index];
      if (currentValue === '' || currentValue.length === 0) {
        e.preventDefault();
        e.stopPropagation();
        handleRemoveTag(index);
      }
    }
  };

  const handleTagPressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleAddTag();
  };

  const containerStyle = scrollable && maxHeight ? { maxHeight, overflowY: 'auto' as const } : undefined;

  const getDuplicateIndices = () => {
    if (!value) return new Set<number>();

    const duplicateIndices = new Set<number>();
    const seen = new Map<string, number[]>();

    value.forEach((tag, index) => {
      const trimmedTag = tag.trim();
      if (trimmedTag === '') return;

      if (!seen.has(trimmedTag)) {
        seen.set(trimmedTag, [index]);
      } else {
        seen.get(trimmedTag)?.push(index);
      }
    });

    seen.forEach((indices) => {
      if (indices.length > 1) {
        indices.forEach((idx) => duplicateIndices.add(idx));
      }
    });

    return duplicateIndices;
  };

  const duplicateIndices = getDuplicateIndices();

  return (
    <div className={styles.wrapper}>
      <div ref={containerRef} className={styles.inputsContainer} style={containerStyle}>
        {value?.map((tag, index) => {
          const isDuplicate = duplicateIndices.has(index) && tag.trim() !== '';
          return (
            <div key={index} className={styles.rowGroup}>
              <div className={styles.inputRow}>
                <div className={styles.inputWrapper}>
                  <Input
                    ref={(el) => {
                      tagInputRefs.current[index] = el?.input || null;
                    }}
                    value={tag}
                    onChange={(e) => handleTagChange(index, e.target.value)}
                    onKeyDown={(e) => handleTagKeyDown(index, e)}
                    onPressEnter={handleTagPressEnter}
                    placeholder={placeholder}
                    className={styles.input}
                    maxLength={maxLength}
                    allowClear
                    status={isDuplicate ? 'error' : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(index)}
                    className={styles.removeButton}
                    disabled={value.length <= minTags}>
                    <Icon name="trash" size={16} />
                  </button>
                </div>
                {isDuplicate && (
                  <div className={styles.duplicateErrorText} role="alert">
                    {DUPLICATE_ERROR_TEXT}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {errorText && <div className={styles.errorText}>{errorText}</div>}

      {showAddButton && (
        <Button
          prefix={<PlusOutlined />}
          onClick={handleAddTag}
          className={styles.addButton}
          variant="outlined"
          color="standard">
          태그 추가
        </Button>
      )}
    </div>
  );
};
